/**
 * Which organisation this browser tab is for.
 *
 * In production the subdomain decides, and the backend reads it from the Host header,
 * so nothing needs sending. Locally everything runs on localhost, so we fall back to
 * an explicit slug and pass it as a header — the same escape hatch the backend's
 * tenant middleware accepts.
 */

export const ROOT_DOMAIN = (process.env.REACT_APP_ROOT_DOMAIN || 'rallyshift.io').toLowerCase();
const OVERRIDE_KEY = 'rallyshift_org_slug';

/**
 * Never a tenant, however the DNS is pointed. Must match RESERVED_SLUGS on the server,
 * which is also what the CHECK constraint in migration 002 enforces.
 */
const RESERVED = new Set([
  'app', 'www', 'api', 'admin', 'auth', 'mail', 'static', 'assets', 'cdn',
  'status', 'docs', 'help', 'support', 'billing', 'dashboard', 'rallyshift',
]);

function slugFromHost(host: string): string | null {
  const name = host.toLowerCase().split(':')[0];
  if (!name.endsWith(`.${ROOT_DOMAIN}`)) return null;

  const sub = name.slice(0, -(ROOT_DOMAIN.length + 1));
  if (!sub || sub.includes('.') || RESERVED.has(sub)) return null;
  return sub;
}

/**
 * A hostname outside ROOT_DOMAIN that could be an organisation's own domain, such as
 * vrijwilligers.wintereventsonenbreugel.nl. Only the server knows which organisation,
 * if any, so the hostname is sent along as X-Organization-Host.
 */
export function customDomainHost(hostname = window.location.hostname): string | null {
  const name = hostname.toLowerCase().split(':')[0];
  if (!name.includes('.') || name.endsWith('.localhost')) return null;
  if (/^\d+(\.\d+){3}$/.test(name)) return null;
  if (name === ROOT_DOMAIN || name.endsWith(`.${ROOT_DOMAIN}`)) return null;
  return name;
}

/** The staff console, which is not a tenant even though it is a subdomain of ROOT_DOMAIN. */
export function isPlatformHost(hostname = window.location.hostname): boolean {
  const name = hostname.toLowerCase().split(':')[0];
  // First label only. `admin.${ROOT_DOMAIN}` would miss admin.localhost.rallyshift.io
  // whenever the bundle was compiled with the production default rallyshift.io, and
  // `startsWith('admin.')` would also match administration.rallyshift.io.
  return name.split('.')[0] === 'admin';
}

export function getOrgSlug(): string | null {
  const fromHost = slugFromHost(window.location.hostname);
  if (fromHost) return fromHost;

  // admin.rallyshift.io names the console, not a customer. A leftover override from a
  // tenant tab would otherwise load that customer's branding on the staff host.
  if (isPlatformHost()) return null;

  // Lets a developer switch tenants without editing .env or touching /etc/hosts.
  const stored = window.localStorage.getItem(OVERRIDE_KEY);
  if (stored) return stored;

  return process.env.REACT_APP_ORG_SLUG || null;
}

export function setOrgSlugOverride(slug: string | null): void {
  if (slug) window.localStorage.setItem(OVERRIDE_KEY, slug);
  else window.localStorage.removeItem(OVERRIDE_KEY);
}

/** True when the host itself identifies the tenant, so no header is needed. */
export function isTenantHost(): boolean {
  return slugFromHost(window.location.hostname) !== null;
}

/**
 * Whether a slug could name an organisation. Only shape is checked here; whether one
 * exists is the server's answer. Matches isValidSlug on the backend.
 */
export function isPlausibleSlug(slug: string): boolean {
  return /^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/.test(slug) && !RESERVED.has(slug);
}

/** Where an organisation lives, keeping the current scheme and port. */
export function tenantUrl(slug: string, path = '/'): string {
  const { protocol, port } = window.location;
  return `${protocol}//${slug}.${ROOT_DOMAIN}${port ? `:${port}` : ''}${path}`;
}
